import React, { useEffect, useRef, useState } from 'react';
import { MemberMobileNav } from './MemberMobileNav';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { SearchBar } from '../common/SearchBar';
import { NotificationBell } from '../common/NotificationBell';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Menu, X, User, LogOut, LayoutDashboard, Trophy, Users, Calendar, Vote, Home, Radio, Shield, Award, Crown, Clock } from 'lucide-react';

// True below Tailwind's 2xl breakpoint, where the hamburger menu is shown.
const COMPACT_QUERY = '(max-width: 1535.98px)';
const getCompactMql = () => (typeof window !== 'undefined' && typeof window.matchMedia === 'function' ? window.matchMedia(COMPACT_QUERY) : null);
const useCompactNav = () => {
  const [compact, setCompact] = useState(() => Boolean(getCompactMql()?.matches));
  useEffect(() => {
    const mql = getCompactMql();
    if (!mql) return undefined;
    const onChange = (e) => setCompact(e.matches);
    setCompact(mql.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);
  return compact;
};

export const Navbar = () => {
  const { user, isAuthenticated, logout, isTrialActive, trialDaysLeft, isPro, isAdminRole } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const isCompactNav = useCompactNav();
  const toggleRef = useRef(null);
  // Signed-in Member on the compact (hamburger) nav only.
  const memberMobile = user?.role === 'member' && isCompactNav;

  useEffect(() => {
    if (!memberMobile || !mobileMenuOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setMobileMenuOpen(false);
        toggleRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [memberMobile, mobileMenuOpen]);

  const onProfileOpenChange = (open) => {
    setProfileOpen(open);
    if (open && memberMobile) setMobileMenuOpen(false);
  };
  const toggleMobileMenu = () => {
    if (!mobileMenuOpen && memberMobile) setProfileOpen(false);
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const navLinks = [
    { href: '/', label: 'Home', icon: Home },
    { href: '/prospects', label: 'Prospects', icon: Users },
    { href: '/draft-pool', label: 'Draft Pool', icon: Vote },
    { href: '/draft-day', label: 'Draft Day', icon: Radio },
    { href: '/teams', label: 'Teams', icon: Users },
    { href: '/games', label: 'Games', icon: Calendar },
    { href: '/leaderboards', label: 'Leaderboards', icon: Trophy },
    { href: '/rankings', label: 'Big Board', icon: Award },
    ...(isAuthenticated ? [{ href: '/forums', label: 'Forums', icon: Shield }] : []),
  ];

  // Phase 2 — hide public homepage navigation for signed-in members and
  // prospects. They have their own role-specific dashboards (Member
  // Hub / Prospect Dashboard) and should not see the public marketing
  // surface. Other authenticated roles (team_admin, FO staff, team
  // athlete) keep the public nav for QA / context.
  const hidePublicNav = ['member', 'prospect'].includes(user?.role);
  const visibleNavLinks = hidePublicNav ? [] : navLinks;

  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getDashboardLink = () => {
    const dashboardRoutes = {
      front_office_staff: '/league-admin',
      team_admin: '/league-admin/prospects',
      prospect: '/prospect',
      team_active_athlete: '/athlete',
      member: '/member', // MEMBER-FREE-ACCESS-1
    };
    return dashboardRoutes[user?.role] || '/';
  };

  const showMemberFeatures = ['member', 'team_active_athlete'].includes(user?.role);
  const showProBadge = showMemberFeatures || ['front_office_staff', 'team_admin'].includes(user?.role);

  return (
    <nav className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b border-border" data-testid="navbar">
      <div className={`max-w-7xl mx-auto px-4 md:px-8 ${isAuthenticated ? '' : '2xl:max-w-[1760px]'}`}>
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="nav-logo" aria-label="Federation Sports home">
            <img
              src="/federation-sports-monogram-v2-96.webp"
              srcSet="/federation-sports-monogram-v2-96.webp 2x, /federation-sports-monogram-v2-144.webp 3x"
              width="48"
              height="48"
              alt="Federation Sports home"
              className="h-11 w-11 md:h-12 md:w-12 object-contain"
            />
            <span className="font-heading font-bold text-xl uppercase tracking-tight whitespace-nowrap hidden sm:block">
              Federation Sports
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden 2xl:flex items-center gap-1">
            {visibleNavLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`nav-link px-4 py-2 font-heading text-sm uppercase tracking-wider ${
                  isActive(link.href) ? 'active text-foreground' : ''
                }`}
                data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Auth Section */}
          <div className="flex items-center gap-3">
            {/* Search Bar - Desktop */}
            <div className="hidden md:block">
              <SearchBar />
            </div>
            
            {isAuthenticated ? (
              <div className="flex items-center gap-1">
                {/* Trial countdown badge — only for member-eligible roles */}
                {showProBadge && isTrialActive && (
                  <Link to="/membership" className="hidden md:flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 mr-1 hover:bg-emerald-500/20 transition-colors" data-testid="trial-badge">
                    <Clock className="w-3.5 h-3.5" />
                    <span className="font-heading text-[11px] uppercase tracking-wider font-bold">
                      {trialDaysLeft}d left
                    </span>
                  </Link>
                )}
                {/* Pro badge — only for member-eligible roles */}
                {showProBadge && !isTrialActive && user?.membership === 'pro' && (
                  <div className="hidden md:flex items-center gap-1 px-2.5 py-1.5 bg-amber-500/10 border border-amber-500/30 text-amber-400 mr-1" data-testid="pro-badge-nav">
                    <Crown className="w-3.5 h-3.5" />
                    <span className="font-heading text-[11px] uppercase tracking-wider font-bold">Pro</span>
                  </div>
                )}
                <NotificationBell />
                <DropdownMenu open={profileOpen} onOpenChange={onProfileOpenChange}>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2 min-h-[44px] lg:min-h-10" data-testid="user-menu-trigger">
                    <div className="w-8 h-8 bg-muted flex items-center justify-center">
                      <User className="w-4 h-4" />
                    </div>
                    <span className="hidden md:block font-heading uppercase tracking-wide text-sm">
                      {user?.name}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                {memberMobile ? (
                <DropdownMenuContent align="end" collisionPadding={8} className="w-56 max-w-[calc(100vw-16px)] bg-card border-border [&_[role=menuitem]]:min-h-[44px]" data-testid="member-account-menu">
                  <DropdownMenuLabel className="font-mono text-[11px] uppercase tracking-[0.25em] text-primary" data-testid="nav-account-heading">
                    Account
                  </DropdownMenuLabel>
                  {showProBadge && !isAdminRole && (
                    <DropdownMenuItem disabled className="flex items-center gap-2 opacity-90 cursor-default" data-testid="nav-membership-status">
                      <Crown className="w-4 h-4 text-amber-400" />
                      <span className="font-mono text-[11px] uppercase tracking-wider">Free Access Active</span>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="flex items-center gap-2 text-destructive" data-testid="nav-logout">
                    <LogOut className="w-4 h-4" />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
                ) : (
                <DropdownMenuContent align="end" className="w-48 bg-card border-border [&_[role=menuitem]]:min-h-[44px] lg:[&_[role=menuitem]]:min-h-0">
                  <DropdownMenuItem asChild>
                    <Link to={getDashboardLink()} className="flex items-center gap-2" data-testid="nav-dashboard">
                      <LayoutDashboard className="w-4 h-4" />
                      Dashboard
                    </Link>
                  </DropdownMenuItem>
                  {/* Member Hub — only for members & team athletes, NOT prospects.
                      MEMBER-FREE-ACCESS-1 — Pro gate removed; every member lands on /member. */}
                  {showMemberFeatures && (
                    <DropdownMenuItem asChild>
                      <Link
                        to={!user?.selected_team_id ? '/select-team' : '/member'}
                        className="flex items-center gap-2"
                        data-testid="nav-member-hub"
                      >
                        <Shield className="w-4 h-4" />
                        {user?.selected_team_id ? 'Member Hub' : 'Join a Team'}
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {user?.role === 'front_office_staff' && (
                    <DropdownMenuItem asChild>
                      <Link to="/league-admin" className="flex items-center gap-2" data-testid="nav-league-admin">
                        <Shield className="w-4 h-4" />
                        League Admin
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {user?.role === 'team_admin' && (
                    <DropdownMenuItem asChild>
                      <Link to="/league-admin/prospects" className="flex items-center gap-2" data-testid="nav-team-admin">
                        <Shield className="w-4 h-4" />
                        Team Admin
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {/* Go Pro / Membership — only for member-eligible roles, NOT prospects.
                      Phase 8.4 — admin roles see a non-clickable "Staff Preview
                      Access" label instead of being sent to Stripe checkout.
                      PAYMENT-SEC-1 — Federation Sports is not currently accepting
                      Member payments. Non-admins see a non-clickable "Free
                      Access Active" label; the /membership route itself still
                      exists but the checkout button on that page is disabled. */}
                  {showProBadge && (
                    isAdminRole ? (
                      <DropdownMenuItem
                        disabled
                        className="flex items-center gap-2 opacity-90 cursor-default"
                        data-testid="nav-staff-preview-status"
                      >
                        <Shield className="w-4 h-4 text-amber-400" />
                        <span className="font-mono text-[11px] uppercase tracking-wider">
                          Staff Preview Access Active
                        </span>
                      </DropdownMenuItem>
                    ) : (
                      <DropdownMenuItem
                        disabled
                        className="flex items-center gap-2 opacity-90 cursor-default"
                        data-testid="nav-membership-status"
                      >
                        <Crown className="w-4 h-4 text-amber-400" />
                        <span className="font-mono text-[11px] uppercase tracking-wider">
                          Free Access Active
                        </span>
                      </DropdownMenuItem>
                    )
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="flex items-center gap-2 text-destructive" data-testid="nav-logout">
                    <LogOut className="w-4 h-4" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
                )}
              </DropdownMenu>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Link to="/become-a-prospect" className="hidden md:block">
                  <Button
                    variant="outline"
                    className="font-heading uppercase tracking-wider text-sm border-primary/40 text-primary hover:bg-primary/10 hover:border-primary/60"
                    data-testid="nav-become-prospect"
                  >
                    Become a Prospect
                  </Button>
                </Link>
                <Link to="/become-a-member?source=nav#founding-member-waitlist" className="hidden md:block">
                  <Button
                    variant="outline"
                    className="font-heading uppercase tracking-wider text-sm border-primary/40 text-primary hover:bg-primary/10 hover:border-primary/60"
                    data-testid="nav-member-waitlist"
                  >
                    Member Waitlist
                  </Button>
                </Link>
                <Link to="/login">
                  <Button variant="ghost" className="font-heading uppercase tracking-wider text-sm" data-testid="nav-login">
                    Login
                  </Button>
                </Link>
                <Link to="/register">
                  <Button className="bg-primary hover:bg-primary/90 font-heading uppercase tracking-wider text-sm" data-testid="nav-register">
                    Join
                  </Button>
                </Link>
              </div>
            )}

            {/* Mobile Menu Toggle */}
            <Button
              variant="ghost"
              size="icon"
              className="2xl:hidden min-h-[44px] min-w-[44px]"
              ref={toggleRef}
              onClick={toggleMobileMenu}
              data-testid="mobile-menu-toggle"
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="2xl:hidden py-4 border-t border-border" data-testid="mobile-menu">
            {memberMobile && (
              <MemberMobileNav
                teamId={user?.selected_team_id}
                pathname={location.pathname}
                onNavigate={() => setMobileMenuOpen(false)}
              />
            )}
            {/* Search — hidden from header below md; always exposed here for
                the compact nav. */}
            <div className="px-4 pb-3 md:hidden [&_input]:w-full [&_input]:h-11 [&_input]:text-base" data-testid="mobile-menu-search">
              <SearchBar />
            </div>
            {visibleNavLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`flex items-center gap-3 px-4 py-3 font-heading uppercase tracking-wider text-sm ${
                  isActive(link.href) ? 'text-primary bg-primary/10' : 'text-muted-foreground'
                }`}
                onClick={() => setMobileMenuOpen(false)}
                data-testid={`mobile-nav-${link.label.toLowerCase().replace(' ', '-')}`}
              >
                <link.icon className="w-5 h-5" />
                {link.label}
              </Link>
            ))}
            {/* Become a Prospect — hidden from header below md; expose here
                for compact nav so no auth action is unreachable. */}
            {!isAuthenticated && (
              <Link
                to="/become-a-prospect"
                className="flex items-center gap-3 px-4 py-3 font-heading uppercase tracking-wider text-sm text-primary md:hidden"
                onClick={() => setMobileMenuOpen(false)}
                data-testid="mobile-nav-become-prospect"
              >
                <Users className="w-5 h-5" />
                Become a Prospect
              </Link>
            )}
            {!isAuthenticated && (
              <Link
                to="/become-a-member?source=nav#founding-member-waitlist"
                className="flex items-center gap-3 px-4 py-3 font-heading uppercase tracking-wider text-sm text-primary md:hidden"
                onClick={() => setMobileMenuOpen(false)}
                data-testid="mobile-nav-member-waitlist"
              >
                <Users className="w-5 h-5" />
                Member Waitlist
              </Link>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};
